import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

import pfp from 'assets/pfp.png';
import userIcon from 'assets/windowsIcons/user.png';

// Windows Messenger — one live lobby for everyone on the site.
// Backend: chat/ (Cloudflare Worker + Durable Object). Wire protocol is JSON
// frames: {t:'hello'|'nick'|'msg'|'nudge'|'ping'} up, {t:'history'|'msg'|
// 'presence'|'nudge'|'welcome'|'error'|'system'|'pong'} down.

export const CHAT_HOST = 'internet-coin-chat.team-27f.workers.dev';
const WS_URL = `wss://${CHAT_HOST}/ws`;
const NICK_KEY = 'ic_messenger_nick';
const UID_KEY = 'ic_messenger_uid';
const MAX_LEN = 240;
const MAX_ROWS = 300;

function loadNick() {
  try {
    return localStorage.getItem(NICK_KEY) || '';
  } catch (e) {
    return '';
  }
}
function saveNick(n) {
  try {
    localStorage.setItem(NICK_KEY, n);
  } catch (e) {
    /* private mode etc. */
  }
}
// Per-browser id -> the server derives a #tag from it, so a nick alone is
// never identity (no "DEV"/"Admin" impersonation).
function loadUid() {
  try {
    let u = localStorage.getItem(UID_KEY);
    if (!u) {
      const a = new Uint8Array(16);
      window.crypto.getRandomValues(a);
      u = Array.from(a, b => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(UID_KEY, u);
    }
    return u;
  } catch (e) {
    return `${Date.now().toString(16)}${Math.random()
      .toString(16)
      .slice(2)}`;
  }
}
function randomNick() {
  return `fren${Math.floor(1000 + Math.random() * 9000)}`;
}
function fmtTime(ts) {
  const d = new Date(ts);
  let h = d.getHours();
  const m = `${d.getMinutes()}`.padStart(2, '0');
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ap}`;
}

// The window chrome is two levels up (.app__content -> StyledWindow). We
// animate margins so we never touch the transform that positions the window.
function shakeWindow(el) {
  const content = el && el.closest('.app__content');
  const target = (content && content.parentElement) || el;
  if (!target || !target.animate) return;
  const frames = [];
  for (let i = 0; i < 12; i += 1) {
    frames.push({
      marginLeft: `${i % 2 ? -7 : 7}px`,
      marginTop: `${i % 3 ? -4 : 4}px`,
    });
  }
  frames.push({ marginLeft: '0px', marginTop: '0px' });
  target.animate(frames, { duration: 650, easing: 'linear' });
}

function useSounds(rootRef) {
  const ctxRef = useRef(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    function unlock() {
      if (!ctxRef.current) {
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (AC) ctxRef.current = new AC();
        } catch (e) {
          /* no audio */
        }
      }
      if (ctxRef.current && ctxRef.current.state === 'suspended') {
        ctxRef.current.resume();
      }
    }
    el.addEventListener('pointerdown', unlock);
    el.addEventListener('keydown', unlock);
    return () => {
      el.removeEventListener('pointerdown', unlock);
      el.removeEventListener('keydown', unlock);
    };
  }, [rootRef]);
  const tone = useCallback((freqs, dur, type = 'square', gain = 0.035) => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.state !== 'running') return;
    let t = ctx.currentTime;
    freqs.forEach(f => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = f;
      g.gain.value = gain;
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t);
      o.stop(t + dur);
      t += dur;
    });
  }, []);
  return {
    ding: () => tone([880, 1175], 0.09),
    buzz: () => tone([110, 92, 110, 92], 0.11, 'sawtooth', 0.05),
  };
}

const CLUE_KEY = 'ic:arg:clue1';
const CLUE_DELAY_MS = 40000;

export default function Messenger() {
  const rootRef = useRef(null);
  const histRef = useRef(null);
  const wsRef = useRef(null);
  const [nick, setNick] = useState(() => loadNick() || randomNick());
  const nickRef = useRef(nick);
  const [nickDraft, setNickDraft] = useState(nick);
  const [tag, setTag] = useState('');
  const [frozen, setFrozen] = useState(false);
  const uidRef = useRef(loadUid());
  const [rows, setRows] = useState([]);
  const [online, setOnline] = useState(0);
  const [status, setStatus] = useState('connecting');
  const [text, setText] = useState('');
  const [lastAt, setLastAt] = useState(null);
  const sounds = useSounds(rootRef);
  const lastDing = useRef(0);

  useEffect(() => {
    nickRef.current = nick;
    saveNick(nick);
  }, [nick]);

  const push = useCallback(row => {
    setRows(prev => {
      const next = [...prev, row];
      return next.length > MAX_ROWS ? next.slice(next.length - MAX_ROWS) : next;
    });
  }, []);

  // ARG level 1 (2026-09-08): a strange message from "the internet", once per browser, ~40 s after
  // the first welcome. Local only — never sent through the socket, nobody else sees it.
  const clueRef = useRef(null);
  function scheduleClue() {
    if (clueRef.current) return;
    let seen = false;
    try {
      seen = localStorage.getItem(CLUE_KEY) === '1';
    } catch (e) {
      /* private mode */
    }
    if (seen) return;
    clueRef.current = setTimeout(() => {
      push({
        id: `arg${Date.now()}`,
        kind: 'msg',
        nick: 'the internet',
        tag: '0000',
        text: "psst. fren. someone left a folder on this machine that shouldn't exist. open Command Prompt on the desktop and type: dir /a — then read what's inside. don't tell the others. (real)",
        ts: Date.now(),
      });
      try {
        localStorage.setItem(CLUE_KEY, '1');
      } catch (e) {
        /* private mode */
      }
      sounds.ding();
    }, CLUE_DELAY_MS);
  }
  useEffect(() => () => clearTimeout(clueRef.current), []);

  // Frame handler lives in a ref so the socket effect never goes stale.
  const handleRef = useRef(null);
  handleRef.current = f => {
    switch (f.t) {
      case 'history':
        setRows((f.msgs || []).map(m => ({ ...m, kind: 'msg' })));
        if (typeof f.frozen === 'boolean') setFrozen(f.frozen);
        break;
      case 'frozen':
        setFrozen(!!f.on);
        break;
      case 'msg': {
        const m = f.m;
        if (!m) break;
        push({ ...m, kind: 'msg' });
        setLastAt(m.ts);
        const mine = m.nick === nickRef.current && (!m.tag || m.tag === tag);
        if (!mine && Date.now() - lastDing.current > 800) {
          lastDing.current = Date.now();
          sounds.ding();
        }
        break;
      }
      case 'presence':
        setOnline(f.n || 0);
        break;
      case 'welcome':
        if (f.nick && f.nick !== nickRef.current) {
          setNick(f.nick);
          setNickDraft(f.nick);
        }
        if (typeof f.n === 'number') setOnline(f.n);
        if (f.tag) setTag(f.tag);
        scheduleClue();
        break;
      case 'nudge':
        push({ id: `n${Date.now()}`, kind: 'sys', text: `${f.nick} just sent you a nudge!`, ts: Date.now() });
        shakeWindow(rootRef.current);
        sounds.buzz();
        break;
      case 'system':
        push({ id: `s${Date.now()}`, kind: 'sys', text: f.text, ts: Date.now() });
        break;
      case 'error':
        push({ id: `e${Date.now()}`, kind: 'err', text: f.text || 'error', ts: Date.now() });
        break;
      default:
    }
  };

  useEffect(() => {
    let ws;
    let closed = false;
    let attempt = 0;
    let timer;
    function connect() {
      setStatus('connecting');
      try {
        ws = new WebSocket(WS_URL);
      } catch (e) {
        setStatus('offline');
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        attempt = 0;
        setStatus('online');
        ws.send(
          JSON.stringify({ t: 'hello', nick: nickRef.current, uid: uidRef.current }),
        );
      };
      ws.onmessage = e => {
        let f;
        try {
          f = JSON.parse(e.data);
        } catch (err) {
          return;
        }
        if (f && handleRef.current) handleRef.current(f);
      };
      ws.onclose = () => {
        if (closed) return;
        setStatus('offline');
        attempt += 1;
        timer = setTimeout(connect, Math.min(15000, 1000 * 2 ** attempt));
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch (e) {
          /* ignore */
        }
      };
    }
    connect();
    // Safari kills sockets in background tabs; come back instantly, not after backoff.
    function wake() {
      if (document.visibilityState === 'hidden') return;
      if (!ws || ws.readyState === 3) {
        clearTimeout(timer);
        attempt = 0;
        connect();
      }
    }
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    const ping = setInterval(() => {
      if (ws && ws.readyState === 1) ws.send('{"t":"ping"}');
    }, 25000);
    return () => {
      closed = true;
      clearTimeout(timer);
      clearInterval(ping);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      try {
        ws.close();
      } catch (e) {
        /* ignore */
      }
    };
  }, []);

  useEffect(() => {
    const el = histRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [rows]);

  function sendFrame(frame) {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) {
      push({ id: `e${Date.now()}`, kind: 'err', text: 'not connected — reconnecting…', ts: Date.now() });
      return false;
    }
    ws.send(JSON.stringify(frame));
    return true;
  }
  function send() {
    const t = text.trim();
    if (!t) return;
    if (sendFrame({ t: 'msg', text: t.slice(0, MAX_LEN) })) setText('');
  }
  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }
  function nudge() {
    if (sendFrame({ t: 'nudge' })) {
      shakeWindow(rootRef.current);
      sounds.buzz();
    }
  }
  function commitNick() {
    const n = nickDraft.trim().slice(0, 20);
    if (!n || n === nick) {
      setNickDraft(nick);
      return;
    }
    setNick(n);
    sendFrame({ t: 'nick', nick: n });
  }

  const statusText =
    status === 'online' ? 'Online' : status === 'connecting' ? 'Signing in…' : 'Offline — reconnecting';

  // MSN groups consecutive lines from the same fren under one "says:" header.
  const rendered = [];
  let prev = null;
  rows.forEach(r => {
    const sameAuthor =
      prev &&
      prev.kind === 'msg' &&
      r.kind === 'msg' &&
      prev.nick === r.nick &&
      prev.tag === r.tag &&
      r.ts - prev.ts < 60000;
    rendered.push({ ...r, showWho: r.kind === 'msg' && !sameAuthor });
    prev = r;
  });

  return (
    <Div ref={rootRef}>
      <div className="im__menubar">
        <span>File</span>
        <span>Edit</span>
        <span>View</span>
        <span>Actions</span>
        <span>Tools</span>
        <span>Help</span>
      </div>
      <div className="im__to">
        <div className="im__to__line">
          <b>To:</b> the internet &lt;everyone on internetcoin.vip&gt;
        </div>
        <div className="im__to__sub">
          <span className={`im__dot im__dot--${status}`} /> {statusText} · {online} online
        </div>
      </div>

      <div className="im__body">
        <div className="im__history" ref={histRef}>
          <div className="im__notice">
            nobody in here is the team. the team never DMs. links and contract
            addresses get removed until launch — the only CA is at /ca.txt.
          </div>
          {rendered.length === 0 && (
            <div className="im__sys">say hi. everyone on the site sees this window.</div>
          )}
          {rendered.map(r => {
            if (r.kind === 'sys') return <div key={r.id} className="im__sys">{r.text}</div>;
            if (r.kind === 'err') return <div key={r.id} className="im__err">{r.text}</div>;
            const me = r.nick === nick && (!r.tag || r.tag === tag);
            return (
              <div key={r.id} className={`im__msg${me ? ' im__msg--me' : ''}`}>
                {r.showWho && (
                  <div className="im__who">
                    {r.nick}
                    {r.tag && <span className="im__tag">#{r.tag}</span>} says:
                  </div>
                )}
                <div className="im__text">{r.text}</div>
              </div>
            );
          })}
        </div>
        <div className="im__dps">
          <div className="im__dp">
            <img src={pfp} alt="the internet" />
          </div>
          <div className="im__dp im__dp--me">
            <img src={userIcon} alt="you" />
          </div>
        </div>
      </div>

      <div className="im__compose">
        <div className="im__tools">
          <button type="button" className="im__nudge" onClick={nudge} title="Send a nudge — shakes everyone's window">
            Nudge
          </button>
          <label className="im__nick">
            You:
            <input
              value={nickDraft}
              disabled={frozen}
              maxLength={20}
              onChange={e => setNickDraft(e.target.value)}
              onBlur={commitNick}
              onKeyDown={e => {
                if (e.key === 'Enter') e.target.blur();
              }}
              spellCheck={false}
            />
          </label>
        </div>
        <div className="im__inputrow">
          <textarea
            value={text}
            maxLength={MAX_LEN}
            onChange={e => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={frozen ? 'chat is paused' : 'type here, press Enter'}
            disabled={frozen}
            spellCheck={false}
          />
          <button type="button" className="im__send" onClick={send}>
            Send
          </button>
        </div>
      </div>
      <div className="im__status">
        {lastAt ? `Last message received at ${fmtTime(lastAt)}.` : 'No messages yet.'}
        <span className="im__count">{text.length}/{MAX_LEN}</span>
      </div>
    </Div>
  );
}

const Div = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #f4ffd0;
  color: #000;
  font-family: Tahoma, sans-serif;
  font-size: 11px;
  min-width: 0;

  .im__menubar {
    display: flex;
    gap: 12px;
    padding: 2px 8px;
    background: #ecf5c9;
    border-bottom: 1px solid #c9dd8a;
    color: #333;
    flex-shrink: 0;
    span:hover {
      color: #566b00;
    }
  }
  .im__to {
    padding: 6px 10px 5px;
    background: linear-gradient(to bottom, #f4ffd0, #dfffa0);
    border-bottom: 1px solid #8fb800;
    flex-shrink: 0;
  }
  .im__to__line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .im__to__sub {
    color: #566b00;
    font-size: 10px;
    margin-top: 2px;
  }
  .im__dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 1px solid #333;
    vertical-align: -1px;
    background: #ccc;
  }
  .im__dot--online {
    background: #5fdc00;
  }
  .im__dot--connecting {
    background: #ffd400;
  }
  .im__dot--offline {
    background: #e03a3a;
  }

  .im__body {
    flex: 1;
    display: flex;
    min-height: 0;
    padding: 6px;
    gap: 6px;
  }
  .im__history {
    flex: 1;
    min-width: 0;
    background: #fff;
    border: 1px solid #8fb800;
    padding: 6px 8px;
    overflow-y: auto;
    overflow-x: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .im__msg {
    margin-bottom: 4px;
  }
  .im__who {
    color: #566b00;
    margin-top: 6px;
  }
  .im__tag {
    color: #9bb14a;
    font-size: 9px;
    margin-left: 2px;
  }
  .im__notice {
    color: #8a7a00;
    background: #fffbe0;
    border: 1px dashed #d6c85a;
    padding: 4px 6px;
    margin-bottom: 6px;
    font-size: 10px;
  }
  .im__msg--me .im__who {
    color: #1d4ed8;
  }
  .im__text {
    padding-left: 14px;
    line-height: 1.4;
    white-space: pre-wrap;
  }
  .im__sys {
    color: #777;
    font-style: italic;
    margin: 4px 0;
  }
  .im__err {
    color: #b00020;
    font-style: italic;
    margin: 4px 0;
  }
  .im__dps {
    width: 78px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .im__dp {
    width: 78px;
    height: 78px;
    border: 1px solid #8fb800;
    background: #0a0c04;
    display: flex;
    align-items: center;
    justify-content: center;
    img {
      width: 62px;
      height: 62px;
      object-fit: contain;
      filter: drop-shadow(0 0 8px rgba(204, 255, 0, 0.6));
    }
  }
  .im__dp--me {
    background: #fff;
    img {
      filter: none;
    }
  }

  .im__compose {
    flex-shrink: 0;
    padding: 0 6px 6px;
  }
  .im__tools {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
  }
  .im__nudge {
    font-family: Tahoma, sans-serif;
    font-size: 11px;
    padding: 2px 10px;
    border: 1px solid #566b00;
    border-radius: 3px;
    background: linear-gradient(to bottom, #f6ffe0, #cfe86a);
    cursor: pointer;
    &:active {
      background: #b0d12b;
    }
  }
  .im__nick {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #566b00;
    input {
      font-family: Tahoma, sans-serif;
      font-size: 11px;
      width: 110px;
      padding: 1px 4px;
      border: 1px solid #8fb800;
      background: #fff;
      outline: none;
    }
  }
  .im__inputrow {
    display: flex;
    gap: 6px;
    height: 54px;
  }
  textarea {
    flex: 1;
    min-width: 0;
    resize: none;
    font-family: Tahoma, sans-serif;
    font-size: 12px;
    padding: 4px 6px;
    border: 1px solid #8fb800;
    outline: none;
    background: #fff;
  }
  .im__send {
    width: 64px;
    font-family: Tahoma, sans-serif;
    font-size: 11px;
    font-weight: bold;
    border: 1px solid #566b00;
    border-radius: 3px;
    background: linear-gradient(to bottom, #e8ff8a, #b0d12b);
    cursor: pointer;
    &:active {
      background: #9bbb1a;
    }
  }
  .im__status {
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    padding: 3px 8px;
    border-top: 1px solid #c9dd8a;
    background: #ecf5c9;
    color: #555;
    font-size: 10px;
  }
  .im__count {
    color: #8fb800;
  }
  @media (max-width: 799px) {
    textarea,
    .im__nick input {
      font-size: 16px; /* iOS: no auto-zoom on focus */
    }
  }
`;
